import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Deep work December",
  "description": "do you ever feel like you're working working working and nothing is happening? Like a game character running against an invisible obstacle? That's how I've been feeling lately. Stuck.",
  "categories": "Personal",
  "published": "2020-12-02T00:00:00.000Z",
  "hero": "./img/screenshot-1606929561380.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Friend, do you ever feel like you're working working working and nothing is happening? Like a game character running against an invisible obstacle?`}</p>
    <p>{`That's how I've been feeling lately. Stuck.`}</p>
    <p>{`There's 3 reasons this happens, in my experience:`}</p>
    <ol>
      <li parentName="ol">{`You're burning out`}</li>
      <li parentName="ol">{`You're working on the wrong things`}</li>
      <li parentName="ol">{`You're a lion chasing mice`}</li>
    </ol>
    <p>{`And if the situation continues, you `}<em parentName="p">{`will`}</em>{` burn out. `}<a parentName="p" {...{
        "href": "https://www.penguinrandomhouse.com/books/593398/peak-performance-by-brad-stulberg-and-steve-magness/"
      }}>{`Peak`}</a>{`, a great book on burnout and performance, says: `}<em parentName="p">{`"Burnout happens when sustained effort yields no reward"`}</em>{`.`}</p>
    <lite-youtube {...{
      "videoid": "qTUW5oI6nFw",
      "videostartat": "0"
    }}></lite-youtube>
    <h2 {...{
      "id": "if-youre-burning-out"
    }}>{`If you're burning out`}</h2>
    <p>{`The only solution is rest. Taking time off. Disrupt your habit triggers. Make a change.`}</p>
    <p>{`I noticed this in Carmel over Thanksgiving. Woke up at 6am as usual, hopped on the computer, and ... just coded.`}</p>
    <p>{`It was so nice 😍`}</p>
    <p>{`No pressure to write, no schedule, no specific goal. Just me, my computer, and ideas for improving `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{`.`}</p>
    <p>{`Yes I'm a nerd and I love to code. It was the lack of obligation that was nice.`}</p>
    <p>{`You might remember my `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/freedom-from-obligation"
      }}>{`Freedom From Obligation video`}</a>{` from last year when I took a trip to Yosemite. 2019 Swizec was right, you do need a break every quarter.`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/freedom-from-obligation"
      }}><img parentName="a" {...{
          "src": "https://s3.amazonaws.com/techletter.app/screenshot-1606923959003.png",
          "alt": null
        }}></img></a></p>
    <p>{`2020 killed the quarterly trip idea 😅`}</p>
    <h2 {...{
      "id": "if-youre-working-on-the-wrong-things"
    }}>{`If you're working on the wrong things`}</h2>
    <p>{`Working on the wrong things feels like running against an invisible wall. You're pushing and pushing and nothing is happening.`}</p>
    <p>{`You could be stuck in a hole. So focused on what you're doing that you don't notice it isn't working.`}</p>
    <p>{`What you're missing is hard to say. Could be a bigger opportunity staring you in the face, or a better way of doing things. You won't know until you look.`}</p>
    <p>{`You gotta take a step back and observe.`}</p>
    <p>{`I'm reading `}<a parentName="p" {...{
        "href": "https://greenlights.com/"
      }}>{`Matthew McConaughey's Greenlights`}</a>{` and there's a chapter on how lost he felt after first achieving fame at 23. Nothing felt right.`}</p>
    <p>{`He took a 3 week trip to the Amazon, lived like a native, did the ecstasy, and got away from Hollywood excess. The result was a long career instead of a short flash-in-the-pan.`}</p>
    <h2 {...{
      "id": "if-youre-a-lion-chasing-mice"
    }}>{`If you're a lion chasing mice`}</h2>
    <p>{`One of my favorite metaphors for life talks about working on challenges that are big enough `}<em parentName="p">{`for you`}</em>{`.`}</p>
    <blockquote>
      <p parentName="blockquote">{`A lion is fully capable of capturing, killing, and eating a field mouse. But it turns out that the energy required to do so exceeds the caloric content of the mouse itself. So a lion that spent its day hunting and eating field mice would slowly starve to death. A lion can't live on field mice. A lion needs antelope. Antelope are big animals. They take more speed and strength to capture and kill, and once killed, they provide a feast for the lion and her pride. ... So ask yourself at the end of the day, 'Did I spend today chasing mice or hunting antelope?'`}</p>
    </blockquote>
    <p>{`The tricky part is realizing when a challenge that used to be big enough, isn't anymore. You've grown.`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/these-19-words-are-the-only-selfhelp-and-business-advice-you-need"
      }}>{`Your life is like a multi-variate optimization problem`}</a>{`. A search through unknown territory.`}</p>
    <p><a parentName="p" {...{
        "href": "https://swizec.com/blog/these-19-words-are-the-only-selfhelp-and-business-advice-you-need"
      }}><img parentName="a" {...{
          "src": "https://i.imgur.com/AtUTxth.gif",
          "alt": "hill climbing algorithm"
        }}></img></a></p>
    <p>{`Hill-climbing algorithms are the best solution we've got for those problems. Build an optimization function, follow the slope.`}</p>
    <p>{`And when your slope starts to level off, when the gain between steps is getting smaller, it might be time for a reset. You're reaching a local maximum.`}</p>
    <p>{`Knowing when to reset so you don't get stuck, that's art. Stepping off the upwards trajectory to reset, that's courage.`}</p>
    <h1 {...{
      "id": "deep-work-december"
    }}>{`Deep work December`}</h1>
    <p>{`With all that in mind, I'm taking December off my friend. You can choose to join me or not, this is not a challenge.`}</p>
    <p>{`No more emails, no more livecoding, no more blogs.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Thinking about a December sabbatical from everything. No tweeting, blogging, livecoding, or newsletter, ... `}<br parentName="p"></br><br parentName="p"></br>{`Long-term work only, nothing urgent allowed.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1329593060355837952"
      }}>{`November 20, 2020`}</a></blockquote>
    <p>{`For a month I'm focusing on long-term projects. Everything gunking up the system and pulling in a million directions.`}</p>
    <p>{`The plan is to finish `}<a parentName="p" {...{
        "href": "https://serverlesshandbook.dev"
      }}>{`Serverless Handbook`}</a>{`, 4 chapters left, finish curating the `}<a parentName="p" {...{
        "href": ""
      }}>{`Senior Mindset Series`}</a>{`, write all new docs and tutorials for `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{` v1.0.0, and create something big I've been planning for January.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`The more I work on "Teach people everything that goes into a modern webapp, but *really teach* not just follow steps 1,2,3 without understanding" the more it feels like I'm designing college.`}<br parentName="p"></br><br parentName="p"></br>{`🤨`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1330619563327823872"
      }}>{`November 22, 2020`}</a></blockquote>
    <p>{`I'll publish daily updates on Twitter. Just to keep me honest.`}</p>
    <p>{`If you've read so far, can you hit reply with a few words on why you read my emails?`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      